/**
 * /forgot-password — request a password-reset link.
 *
 * Enumeration-safe: the backend always returns 200 with a constant
 * message, so this page shows the same "check your inbox" confirmation
 * whether or not the email is on file. Works for every role (passenger,
 * driver, admin) — they're all accounts in the same store.
 *
 * Zero dependencies on dispatch / state machine / janitor / offer routing.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Header } from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mail, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      // 429 (rate limited) is the only expected error path; everything else
      // is treated as a soft success to preserve enumeration-safety.
      if (err?.response?.status === 429) {
        toast.error("Too many requests. Please wait a while and try again.");
      } else {
        setSent(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header title="RESET PASSWORD" />
      <div className="flex items-center justify-center px-4 py-16">
        <div className="max-w-md w-full">
          <div className="border border-border bg-card p-8" data-testid="forgot-password-card">
            {sent ? (
              <div className="text-center" data-testid="forgot-password-sent">
                <div className="w-14 h-14 rounded-full bg-[#10B981] mx-auto flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-white" />
                </div>
                <h1 className="font-heading text-2xl font-black mt-4">Check your inbox</h1>
                <p className="text-sm text-muted-foreground mt-2">
                  If an account exists for <strong>{email}</strong>, we&apos;ve sent a password
                  reset link. It expires in 60 minutes.
                </p>
                <p className="text-xs text-muted-foreground mt-3">
                  Didn&apos;t get it? It can take a minute or two — check your spam folder too.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="forgot-password-form">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-10 h-10 rounded-full bg-[#FFC107] flex items-center justify-center">
                      <Mail className="w-5 h-5 text-[#0A0A0A]" />
                    </div>
                  </div>
                  <h1 className="font-heading text-3xl font-black tracking-tight">Forgot your password?</h1>
                  <p className="text-sm text-muted-foreground mt-2">
                    Enter your account email and we&apos;ll send you a link to set a new password.
                  </p>
                </div>
                <div>
                  <Label htmlFor="email" className="label-mini">EMAIL</Label>
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="name@example.com"
                    data-testid="forgot-password-email-input"
                    className="mt-1.5"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full" size="lg" data-testid="forgot-password-submit-btn">
                  {loading ? "Sending…" : "Send reset link"}
                </Button>
              </form>
            )}
            <div className="mt-6 pt-6 border-t border-border text-center">
              <Link to="/login" className="text-xs text-muted-foreground hover:text-foreground" data-testid="forgot-password-back-to-login">
                ← Back to sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
